import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
} from '@angular/core';
import {
  InfoBannerComponent,
  RadioCardComponent,
  RadioCardGroupComponent,
} from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { collapse } from '../../../../assets/animations/animations';
import { StripeProduct } from '../../../interfaces/stripe';
import { NumberStepperComponent } from '../../number-stepper/number-stepper.component';

export const CUSTOM_CREDITS_OPTION = '__custom__';

@Component({
  selector: 'app-credits-package-selector',
  imports: [
    CurrencyPipe,
    InfoBannerComponent,
    NgxSkeletonLoaderModule,
    NumberStepperComponent,
    RadioCardComponent,
    RadioCardGroupComponent,
    TranslocoPipe,
  ],
  templateUrl: './credits-package-selector.component.html',
  styleUrl: './credits-package-selector.component.scss',
  animations: [collapse],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditsPackageSelectorComponent {
  readonly packages = input.required<StripeProduct[]>();
  readonly loaded = input.required<boolean>();
  readonly disabled = input(false);
  readonly selectedOption = model<string | null>(null);
  readonly customPackageCount = model(1);

  readonly themingService = inject(ThemingService);
  readonly CUSTOM_CREDITS_OPTION = CUSTOM_CREDITS_OPTION;

  readonly smallestPackage = computed(() => {
    const sorted = [...this.packages()].sort(
      (first, second) =>
        this.creditsAmountOf(first) - this.creditsAmountOf(second),
    );
    return sorted[0] ?? null;
  });

  readonly largerPackages = computed(() => {
    const smallest = this.smallestPackage();
    return this.packages().filter((pack) => pack.id !== smallest?.id);
  });

  readonly customTotalPrice = computed(
    () =>
      (this.smallestPackage()?.unit_amount ?? 0) *
      this.customPackageCount() *
      0.01,
  );

  /**
   * Whether the custom quantity is high enough that a catalog package may be cheaper.
   * @returns {boolean}
   */
  readonly showPackageHint = computed(() => this.customPackageCount() >= 8);

  /**
   * Resolves the display title for a catalog price.
   * @param {StripeProduct | null} pack Catalog price
   * @returns {string}
   */
  productTitle(pack: StripeProduct | null): string {
    if (!pack) {
      return '';
    }
    return pack.product?.name ?? pack.id;
  }

  /**
   * Reads credits_amount from price or product metadata.
   * @param {StripeProduct | null} pack Catalog price
   * @returns {number}
   */
  creditsAmountOf(pack: StripeProduct | null): number {
    if (!pack) {
      return 0;
    }
    const fromPrice = +(pack.metadata?.['credits_amount'] ?? 0);
    if (fromPrice > 0) {
      return fromPrice;
    }
    const product = pack.product as
      | (StripeProduct['product'] & { metadata?: Record<string, string> })
      | undefined;
    return +(product?.metadata?.['credits_amount'] ?? 0);
  }

  /**
   * Price of a catalog entry in major currency units.
   * @param {StripeProduct | null} pack Catalog price
   * @returns {number}
   */
  displayAmount(pack: StripeProduct | null): number {
    return (pack?.unit_amount ?? 0) / 100;
  }
}
